import { Autocomplete, Box, TextField } from "@mui/material";
import { useAsync2 } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../../api/API";
import { Goal, Project } from "../../../api/Projects";
import { randomColor } from "../../../misc/projectColors";
import { useSnackAction } from "../../../misc/SnackAction";
import { useWorkspace } from "../../../misc/Workspace";

// an id of 0 is the "create one by this name" entry
type Option = { id: number; name: string };

// ChainProjectPicker picks the project a chain goes to, and a goal of it. Either can be
// made by typing a name that matches none.
export function ChainProjectPicker(props: {
    project: Project | null;
    goal: Goal | null;
    onProject: (value: Project | null) => void;
    onGoal: (value: Goal | null) => void;
}) {
    const ws = useWorkspace();
    const [search, setSearch] = useState("");
    const [goalSearch, setGoalSearch] = useState("");

    const projects = useAsync2(input => api.projects.list(input),
        { search: search, archived: false, team: ws.team || undefined }, [search, ws.team]);

    const createProject = useSnackAction(async (input: { name: string }) => {
        const created = await api.projects.upsert({ name: input.name, color: randomColor(), team: ws.newTeam });
        props.onProject(created);
    }, { success: "Project created" });

    const createGoal = useSnackAction(async (input: { name: string }) => {
        if (!props.project) return;

        const created = await api.projects.upsertGoal({ project: props.project.id, name: input.name });
        props.onGoal(created);
    }, { success: "Goal created" });

    const projectOptions: Option[] = (projects.result || []).map(row => ({ id: row.id, name: row.name }));
    const goals = (props.project?.goals || []).filter(goal => !goal.archived);
    const goalOptions: Option[] = goals.map(row => ({ id: row.id, name: row.name }));

    // the create option leads the list, so enter takes it
    const withCreate = (list: Option[], term: string) => {
        const name = term.trim();
        if (!name || list.some(row => row.name.toLowerCase() === name.toLowerCase())) return list;

        return [{ id: 0, name: name }].concat(list);
    };

    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={projectOptions}
                value={props.project ? { id: props.project.id, name: props.project.name } : null}
                disabled={createProject.loading}
                loading={projects.loading}
                onInputChange={(_, value, reason) => {
                    if (reason !== "reset") setSearch(value);
                }}
                filterOptions={list => withCreate(list, search)}
                getOptionLabel={(option: Option) => option.id ? option.name : "Create: " + option.name}
                isOptionEqualToValue={(a: Option, b: Option) => a.id === b.id}
                onChange={(_, value: Option | null) => {
                    setSearch("");
                    props.onGoal(null);

                    if (!value) {
                        props.onProject(null);
                        return;
                    }

                    if (!value.id) {
                        createProject.callback({ name: value.name });
                        return;
                    }

                    props.onProject((projects.result || []).find(row => row.id === value.id) || null);
                }}
                renderInput={params => (
                    <TextField {...params} label="Project" error={!!projects.error}
                               helperText={projects.error || undefined} />
                )}
            />

            <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={goalOptions}
                value={props.goal ? { id: props.goal.id, name: props.goal.name } : null}
                disabled={!props.project || createGoal.loading}
                onInputChange={(_, value, reason) => {
                    if (reason !== "reset") setGoalSearch(value);
                }}
                filterOptions={list => withCreate(
                    list.filter(row => row.name.toLowerCase().includes(goalSearch.trim().toLowerCase())),
                    goalSearch)}
                getOptionLabel={(option: Option) => option.id ? option.name : "Create: " + option.name}
                isOptionEqualToValue={(a: Option, b: Option) => a.id === b.id}
                onChange={(_, value: Option | null) => {
                    setGoalSearch("");

                    if (!value) {
                        props.onGoal(null);
                        return;
                    }

                    if (!value.id) {
                        createGoal.callback({ name: value.name });
                        return;
                    }

                    props.onGoal(goals.find(row => row.id === value.id) || null);
                }}
                renderInput={params => <TextField {...params} label="Goal (optional)" />}
            />
        </Box>
    );
}
